import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import MasterLayout from '../layouts/masterlayout';
import ErrorMessage from '../common/errormessage';
import SuccessMessage from '../common/successmessage';
import GeneralButton from '../common/button_general';
import Spinner from '../common/spinner';
import { getHeaders, handleErrorResponse } from '../../lib/requests';

function ClientImport() {
  const [importText, setImportText] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [importResults, setImportResults] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const apiToken = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_API_URL;
      const headers = getHeaders(apiToken);
      const response = await axios.get(`${apiUrl}/clients`, { headers });
      setClients(response.data.data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowError(false);
    setShowSuccess(false);
    setErrorMessage('');
    setSuccessMessage('');
    setImportResults(null);
    setIsLoading(true);

    if (!selectedClientId) {
      setErrorMessage('Please select a provider');
      setShowError(true);
      setIsLoading(false);
      return;
    }

    try {
      const apiToken = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_API_URL;
      const headers = getHeaders(apiToken);

      const payload = {
        importData: importText,
        clientId: selectedClientId,
        providerId: null
      };

      const response = await axios.post(`${apiUrl}/customers/import`, payload, { headers });

      setImportResults(response.data);
      setSuccessMessage(`Import completed: ${response.data.summary.created} created, ${response.data.summary.updated} updated`);
      setShowSuccess(true);

      // Remove successfully processed rows from the import text
      if (response.data.results && response.data.results.length > 0) {
        const lines = importText.split('\n');
        const successfulNames = new Set(
          response.data.results.map(r => r.name.toLowerCase())
        );

        // Keep the header row and filter out successful rows
        const filteredLines = lines.filter((line, index) => {
          if (index === 0) return true; // Keep header

          // Extract name from line (first two columns are Last Name, First Name)
          const columns = line.split('\t');
          if (columns.length < 2) return true;

          const lastName = columns[0]?.trim();
          const firstName = columns[1]?.trim();
          const fullName = `${firstName} ${lastName}`.toLowerCase();

          return !successfulNames.has(fullName);
        });

        setImportText(filteredLines.join('\n'));
      } else {
        setImportText('');
      }
    } catch (error) {
      handleErrorResponse(error, navigate, setShowError, setErrorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => navigate('/clients');

  const sampleData = `Last Name	First Name	Middle Name/Initial	Suffix	Provider Route	Number of Meals	Mechanically Altered	Delivery Day	Delivery Schedule	Current Menu	Internal Notes	Provider Notes	Status
Smith	John	A	Jr	Route 1	7	None	Monday	Weekly	Regular	No special instructions	Leave at side door	ACTIVE
Johnson	Mary	B		Route 2	14	Chopped	Tuesday	Biweekly	Diabetic	Requires low sodium	Call before delivery	ACTIVE
Brown	Robert			Route 1	7	Ground	Wednesday	Weekly	Regular	Prefers afternoon delivery	Ring doorbell twice	HOLD 06/13/25
Davis	Susan			Route 3	10	None	Friday	Monthly	Vegetarian	Monthly delivery preferred		DECEASED`;

  return (
    <MasterLayout pageTitle="Import Clients">
      {showError && <ErrorMessage message={errorMessage} />}
      {showSuccess && <SuccessMessage message={successMessage} />}
      {isLoading && <Spinner />}

      {!isLoading && (
        <div className="p-4">
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <div>
                <label htmlFor="clientSelect" className="block mb-2 text-sm font-medium text-gray-900">
                  Provider (Required) *
                </label>
                <select
                  id="clientSelect"
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                  required
                >
                  <option value="">Select a provider...</option>
                  {clients.map((client) => (
                    <option key={client.client_id} value={client.client_id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="importText" className="block mb-2 text-sm font-medium text-gray-900">
                Paste tab-delimited client data
              </label>
              <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800 font-medium">Required Format (tab-delimited):</p>
                <p className="text-xs text-blue-600 font-mono">
                  Last Name	First Name	Middle Name/Initial	Suffix	Provider Route	Number of Meals	Mechanically Altered	Delivery Day	Delivery Schedule	Current Menu	Internal Notes	Provider Notes	Status
                </p>
              </div>
              <textarea
                id="importText"
                name="importText"
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                rows={12}
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 font-mono"
                placeholder={sampleData}
                required
              />
              <div className="mt-2 text-xs text-gray-600">
                <p><strong>Import Instructions:</strong></p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li><strong>Headers:</strong> First row must contain headers exactly as shown above (13 columns total)</li>
                  <li><strong>Mechanically Altered:</strong> Options are None, Chopped, Ground, Pureed (case insensitive)</li>
                  <li><strong>Delivery Day:</strong> Specify day name only (Monday, Tuesday, Wednesday, Thursday, Friday, Saturday, Sunday)</li>
                  <li><strong>Delivery Schedule:</strong> Separate column for schedule frequency (Weekly, Biweekly, Monthly)</li>
                  <li><strong>Current Menu:</strong> Will be matched against existing menu names (partial matches allowed)</li>
                  <li><strong>Status:</strong> Options are ACTIVE (default if not provided), DECEASED, DISENROLLED, or HOLD (dates like &quot;HOLD 06/13/25&quot; will be stored as &quot;hold&quot;)</li>
                  <li><strong>Updates:</strong> Existing clients (same first/last name and provider) will be updated</li>
                </ul>
              </div>
            </div>

            <div className="flex justify-center mt-6 gap-2">
              <GeneralButton type="submit" buttonType="primary">
                Import Clients
              </GeneralButton>
              <GeneralButton type="button" buttonType="secondary" onClick={handleBack}>
                Back to Client List
              </GeneralButton>
            </div>
          </form>

          {importResults && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Import Results</h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="text-center p-3 bg-green-100 rounded">
                  <div className="text-2xl font-bold text-green-600">{importResults.summary.created}</div>
                  <div className="text-sm text-green-700">Created</div>
                </div>
                <div className="text-center p-3 bg-blue-100 rounded">
                  <div className="text-2xl font-bold text-blue-600">{importResults.summary.updated}</div>
                  <div className="text-sm text-blue-700">Updated</div>
                </div>
                <div className="text-center p-3 bg-red-100 rounded">
                  <div className="text-2xl font-bold text-red-600">{importResults.summary.errors}</div>
                  <div className="text-sm text-red-700">Errors</div>
                </div>
              </div>

              {importResults.errors && importResults.errors.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-red-700 mb-2">Errors:</h4>
                  <ul className="text-sm text-red-600 list-disc list-inside space-y-1">
                    {importResults.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {importResults.results && importResults.results.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Successfully Processed:</h4>
                  <div className="max-h-40 overflow-y-auto">
                    <ul className="text-sm text-gray-600 space-y-1">
                      {importResults.results.map((result, index) => (
                        <li key={index} className="flex justify-between">
                          <span>{result.name}</span>
                          <span className={`px-2 py-1 rounded text-xs ${
                            result.action === 'created'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {result.action}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </MasterLayout>
  );
}

export default ClientImport;